/**
 * The Film Brain — 1895's living context graph.
 *
 * The single source of creative + production truth for a film: derive it from the
 * script, watch its readiness, and clear the gaps that block production — each
 * routed to the right department. Resolving a gap authors the Brain, so the
 * shot generator immediately conditions on the new decision.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Brain, Loader2, RefreshCw, Sparkles, Clock, MapPin, Shirt, Users, Film,
  Zap, Check, X, AlertTriangle, Layers, GitBranch, Camera, Gauge,
} from "lucide-react";
import {
  deriveBrain, getBrainRun, getBrainOverview, getGaps, resolveGap, scanGaps,
} from "@/services/brainService";

const ROLE_META = {
  producer: { label: "Producer" },
  director: { label: "Director" },
  assistant_director: { label: "1st AD" },
  cinematographer: { label: "Cinematographer" },
  production_designer: { label: "Prod. Designer" },
  costume_designer: { label: "Costume" },
};

const IMPACT = {
  high: { label: "High", cls: "text-[var(--color-error)] border-[rgba(244,63,94,0.4)] bg-[rgba(244,63,94,0.1)]" },
  medium: { label: "Medium", cls: "text-[var(--color-gold-400)] border-[rgba(212,168,83,0.4)] bg-[rgba(212,168,83,0.1)]" },
  low: { label: "Low", cls: "text-[var(--text-disabled)] border-[var(--border-default)] bg-[var(--bg-muted)]" },
};

const NODE_ICON = {
  scene: Film, character: Users, location: MapPin, prop: Layers,
  plot_thread: GitBranch, style_token: Sparkles, motif: Sparkles, film: Film,
};

function pct(x) {
  return Math.round((x || 0) * 100);
}

function ReadinessRing({ value = 0, size = 88 }) {
  const r = (size - 12) / 2;
  const c = 2 * Math.PI * r;
  const p = Math.max(0, Math.min(1, value));
  const stroke = p >= 0.75 ? "var(--color-success)" : p >= 0.4 ? "var(--color-gold-500)" : "var(--color-error)";
  return (
    <svg width={size} height={size} className="-rotate-90">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-muted)" strokeWidth="6" />
      <motion.circle
        cx={size / 2} cy={size / 2} r={r} fill="none" stroke={stroke} strokeWidth="6"
        strokeLinecap="round" strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c - c * p }}
        transition={{ duration: 0.8, ease: "easeOut" }}
      />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        className="rotate-90" style={{ transformOrigin: "center", fill: "var(--text-primary)", fontSize: 18, fontWeight: 700 }}>
        {pct(value)}%
      </text>
    </svg>
  );
}

function StatCard({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: accent || "var(--bg-muted)" }}>
        <Icon size={18} className="text-[var(--text-primary)]" />
      </div>
      <div>
        <div className="text-2xl font-bold text-[var(--text-primary)] leading-none">{value}</div>
        <div className="text-xs text-[var(--text-secondary)] mt-1">{label}</div>
      </div>
    </div>
  );
}

function GapCard({ gap, onApply, onDismiss, busy }) {
  const [val, setVal] = useState(gap.suggested ?? "");
  const impact = IMPACT[gap.impact] || IMPACT.low;
  const role = ROLE_META[gap.role]?.label || gap.role;
  // `location` needs a real location id we can't pick here yet — route to Locations tab.
  const isLocation = gap.field === "location";
  const needsValue = ["time_of_day", "time_of_day_confirm", "mood"].includes(gap.field)
    || gap.field.startsWith("wardrobe:");
  return (
    <motion.div
      layout initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
      className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-3.5"
      data-testid={`gap-${gap.key}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${impact.cls}`}>{impact.label}</span>
            <span className="text-[10px] text-[var(--text-secondary)] bg-[var(--bg-muted)] px-1.5 py-0.5 rounded">{role}</span>
            <span className="text-[10px] text-[var(--text-disabled)]">Scene {gap.scene_number}</span>
            {gap.kind === "logistical" && (
              <span className="text-[10px] text-[var(--color-gold-400)] flex items-center gap-0.5"><Zap size={10} /> production</span>
            )}
          </div>
          <p className="text-sm text-[var(--text-primary)] font-medium mt-1.5 truncate">{gap.title}</p>
          {gap.detail && <p className="text-xs text-[var(--text-secondary)] mt-0.5">{gap.detail}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 mt-3">
        {isLocation ? (
          <span className="text-[11px] text-[var(--text-secondary)] flex items-center gap-1 flex-1">
            <MapPin size={12} /> Bind this scene's location in the Locations tab
          </span>
        ) : (
          <>
            {needsValue && (
              <Input
                value={val} onChange={(e) => setVal(e.target.value)}
                placeholder={gap.suggested ? `Suggested: ${gap.suggested}` : "Enter value…"}
                className="h-8 text-xs bg-[var(--bg-base)] border-[var(--border-default)]"
              />
            )}
            <Button
              size="sm" disabled={busy}
              onClick={() => onApply(gap, needsValue ? val : (gap.suggested || "acknowledged"))}
              className="h-8 text-xs bg-[var(--color-gold-500)] text-[var(--color-base-950)] hover:bg-[var(--color-gold-400)] whitespace-nowrap"
              data-testid={`gap-apply-${gap.key}`}
            >
              <Check size={13} className="mr-1" /> {needsValue ? "Apply" : "Mark handled"}
            </Button>
          </>
        )}
        <Button
          size="sm" variant="ghost" disabled={busy}
          onClick={() => onDismiss(gap)}
          className="h-8 text-xs text-[var(--text-disabled)] hover:text-[var(--text-primary)]"
          data-testid={`gap-dismiss-${gap.key}`}
        >
          <X size={13} />
        </Button>
      </div>
    </motion.div>
  );
}

export default function FilmBrainPage({ projectId, project, script, scenes = [], onUpdate }) {
  const [overview, setOverview] = useState(null);
  const [gaps, setGaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deriving, setDeriving] = useState(false);
  const [run, setRun] = useState(null);
  const [roleFilter, setRoleFilter] = useState("all");
  const [busyKey, setBusyKey] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [ov, gp] = await Promise.all([
        getBrainOverview(projectId),
        getGaps(projectId, { status: "open" }),
      ]);
      setOverview(ov);
      setGaps(gp.gaps || []);
    } catch (e) {
      console.error("brain load", e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); return () => clearInterval(pollRef.current); }, [load]);

  const pollRun = useCallback((runId) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await getBrainRun(projectId, runId);
        setRun(r);
        if (r.status === "completed" || r.status === "failed") {
          clearInterval(pollRef.current);
          setDeriving(false);
          if (r.status === "completed") {
            toast.success("Film Brain derived");
            await load();
            onUpdate?.();
          } else {
            toast.error(r.error || "Derivation failed");
          }
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
  }, [projectId, load, onUpdate]);

  const handleDerive = async () => {
    if (!script?.scenes?.length && !scenes.length) {
      toast.error("Upload & parse a script first");
      return;
    }
    setDeriving(true);
    setRun({ status: "processing", progress: 0, current_step: "Queued…" });
    try {
      const { run_id } = await deriveBrain(projectId, { enrich: true });
      pollRun(run_id);
    } catch (e) {
      setDeriving(false);
      toast.error(e.response?.data?.detail || "Could not start derivation");
    }
  };

  const handleApply = async (gap, value) => {
    setBusyKey(gap.key);
    try {
      await resolveGap(projectId, gap.key, { value });
      setGaps((g) => g.filter((x) => x.key !== gap.key));
      toast.success("Authored to the Brain");
      getBrainOverview(projectId).then(setOverview).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply");
    } finally {
      setBusyKey(null);
    }
  };

  const handleDismiss = async (gap) => {
    setBusyKey(gap.key);
    try {
      await resolveGap(projectId, gap.key, { dismiss: true });
      setGaps((g) => g.filter((x) => x.key !== gap.key));
      getBrainOverview(projectId).then(setOverview).catch(() => {}); // keep role/impact counts in sync
    } catch (e) {
      toast.error("Could not dismiss");
    } finally {
      setBusyKey(null);
    }
  };

  const handleRescan = async () => {
    try {
      const r = await scanGaps(projectId);
      toast.success(`Readiness ${pct(r.summary.film_readiness)}% · ${r.summary.open_gap_count} open gaps`);
      await load();
    } catch (e) {
      toast.error("Scan failed");
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center text-[var(--text-secondary)]">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading the Brain…
      </div>
    );
  }

  const derived = overview?.derived;
  const counts = overview?.counts?.nodes_by_type || {};
  const readiness = overview?.readiness || {};
  const byRole = readiness.open_by_role || {};
  const filtered = roleFilter === "all" ? gaps : gaps.filter((g) => g.role === roleFilter);
  const sortedRoles = Object.keys(ROLE_META).filter((r) => byRole[r]);

  return (
    <div className="h-full overflow-y-auto px-6 py-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[var(--color-gold-500)] to-[var(--color-gold-600)] flex items-center justify-center">
            <Brain size={22} className="text-[var(--color-base-950)]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[var(--text-primary)]">The Film Brain</h1>
            <p className="text-xs text-[var(--text-secondary)]">
              Living context graph — the single source of creative & production truth
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {derived && (
            <Button variant="ghost" size="sm" onClick={handleRescan}
              className="text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="brain-rescan">
              <Gauge size={15} className="mr-1.5" /> Rescan
            </Button>
          )}
          <Button onClick={handleDerive} disabled={deriving}
            className="bg-[var(--color-gold-500)] text-[var(--color-base-950)] hover:bg-[var(--color-gold-400)]"
            data-testid="brain-derive">
            {deriving ? <Loader2 size={16} className="mr-1.5 animate-spin" /> : <RefreshCw size={16} className="mr-1.5" />}
            {derived ? "Re-derive" : "Derive Film Brain"}
          </Button>
        </div>
      </div>

      {/* Derivation progress */}
      <AnimatePresence>
        {deriving && run && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="mb-5 rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 overflow-hidden">
            <div className="flex items-center justify-between text-sm text-[var(--text-primary)] mb-2">
              <span className="flex items-center gap-2"><Sparkles size={15} className="text-[var(--color-gold-400)]" /> {run.current_step}</span>
              <span className="text-[var(--text-secondary)]">{run.progress || 0}%</span>
            </div>
            <div className="h-1.5 bg-[var(--bg-muted)] rounded-full overflow-hidden">
              <motion.div className="h-full bg-[var(--color-gold-500)]" animate={{ width: `${run.progress || 0}%` }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Empty state */}
      {!derived && !deriving && (
        <div className="rounded-2xl border border-dashed border-[var(--border-default)] bg-[var(--bg-surface)] p-10 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-muted)] flex items-center justify-center mx-auto mb-4">
            <Brain size={30} className="text-[var(--color-gold-400)]" />
          </div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Build the film's brain</h2>
          <p className="text-sm text-[var(--text-secondary)] max-w-lg mx-auto mt-2">
            Derive a living graph from your script — scenes, characters and their look over story-time,
            locations, props, plot threads and relationships. It powers consistent shot generation and
            surfaces every production gap before it becomes a reshoot.
          </p>
          <Button onClick={handleDerive} className="mt-5 bg-[var(--color-gold-500)] text-[var(--color-base-950)] hover:bg-[var(--color-gold-400)]">
            <Sparkles size={16} className="mr-1.5" /> Derive Film Brain
          </Button>
        </div>
      )}

      {/* Dashboard */}
      {derived && (
        <div className="space-y-5">
          {/* Stat row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-4 flex items-center gap-4">
              <ReadinessRing value={readiness.film_readiness ?? overview?.latest_run?.stats?.film_readiness ?? 0} />
              <div>
                <div className="text-sm font-semibold text-[var(--text-primary)]">Production readiness</div>
                <div className="text-xs text-[var(--text-secondary)] mt-1">{readiness.open_gap_count ?? gaps.length} open gaps</div>
              </div>
            </div>
            <StatCard icon={Film} label="Scenes" value={counts.scene || 0} />
            <StatCard icon={Users} label="Characters" value={counts.character || 0} />
            <StatCard icon={MapPin} label="Locations" value={counts.location || 0} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={Layers} label="Props" value={counts.prop || 0} />
            <StatCard icon={GitBranch} label="Plot threads" value={counts.plot_thread || 0} />
            <StatCard icon={Camera} label="High-impact gaps" value={readiness.open_by_impact?.high || 0}
              accent="var(--color-error)" />
            <StatCard icon={AlertTriangle} label="Logistical gaps" value={gaps.filter((g) => g.kind === "logistical").length}
              accent="var(--color-gold-600)" />
          </div>

          {/* Gap center */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
                <AlertTriangle size={15} className="text-[var(--color-gold-400)]" /> Gaps — routed by department
              </h2>
            </div>

            {/* Role filter chips */}
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <RoleChip active={roleFilter === "all"} onClick={() => setRoleFilter("all")} label={`All (${gaps.length})`} />
              {sortedRoles.map((r) => (
                <RoleChip key={r} active={roleFilter === r} onClick={() => setRoleFilter(r)}
                  label={`${ROLE_META[r].label} (${byRole[r]})`} />
              ))}
            </div>

            {filtered.length === 0 ? (
              <div className="rounded-xl border border-[var(--border-default)] bg-[var(--bg-surface)] p-8 text-center">
                <Check size={26} className="text-[var(--color-success)] mx-auto mb-2" />
                <p className="text-sm text-[var(--text-primary)] font-medium">No open gaps here</p>
                <p className="text-xs text-[var(--text-secondary)] mt-1">This department is clear. Nice.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-3">
                <AnimatePresence>
                  {filtered.map((g) => (
                    <GapCard key={g.key} gap={g} busy={busyKey === g.key}
                      onApply={handleApply} onDismiss={handleDismiss} />
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>

          {/* Scene readiness strip */}
          <SceneStrip projectId={projectId} scenes={script?.scenes || scenes} gaps={gaps} />
        </div>
      )}
    </div>
  );
}

function RoleChip({ active, onClick, label }) {
  return (
    <button onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? "bg-[var(--color-gold-500)] text-[var(--color-base-950)] border-[var(--color-gold-500)]"
          : "bg-[var(--bg-surface)] text-[var(--text-secondary)] border-[var(--border-default)] hover:text-[var(--text-primary)]"
      }`}>
      {label}
    </button>
  );
}

function SceneStrip({ scenes, gaps }) {
  const openByScene = {};
  gaps.forEach((g) => { openByScene[g.scene_number] = (openByScene[g.scene_number] || 0) + 1; });
  if (!scenes?.length) return null;
  return (
    <div>
      <h2 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2 mb-3">
        <Film size={15} className="text-[var(--color-gold-400)]" /> Scene readiness
      </h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {scenes.map((s) => {
          const open = openByScene[s.number] || 0;
          const ok = open === 0;
          return (
            <div key={s.number}
              className="rounded-lg border border-[var(--border-default)] bg-[var(--bg-surface)] p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs text-[var(--text-disabled)]">Scene {s.number}</div>
                <div className="text-sm text-[var(--text-primary)] truncate">{s.heading || "Untitled"}</div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-[var(--text-secondary)]">
                  {s.time_of_day && <span className="flex items-center gap-0.5"><Clock size={10} /> {s.time_of_day}</span>}
                  {s.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={10} /> {s.location}</span>}
                </div>
              </div>
              <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${
                ok ? "bg-[rgba(74,222,128,0.15)] text-[var(--color-success)]"
                   : "bg-[rgba(212,168,83,0.15)] text-[var(--color-gold-400)]"
              }`}>
                {ok ? <Check size={14} /> : open}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
